import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import CampoTexto from '@/components/Formulario/CampoTexto';
import { CampoSeletorCliente } from '@/components/modelos/CampoSeletorCliente';
import Botao from '@/components/Botao';
import Icon from '@/components/Icon';
import { mdiPhone, mdiAccount, mdiNumeric, mdiClose } from '@mdi/js';
import axios from 'axios';
import styles from './PopupDiscagem.module.css';
import CampoSeletor from '@/components/Formulario/CampoSeletor';

type Cliente = {
    id: number;
    nome: string;
    sobrenome?: string;
    contatos: Array<{
        id: number;
        tipo: string;
        valor: string;
    }>;
};

type Canal = {
    id: number;
    nome: string;
    tipo: string;
    chave_api?: string;
};

type PhoneNumberFormat = 'E164' | 'N10';

type PopupDiscagemProps = {
    show: boolean;
    onClose: () => void;
    onCall: (params: {
        clienteId: number;
        clienteNome: string;
        telefone: string;
        canalId: number;
    }) => void;
    canaisVoip: Canal[];
};

export const PopupDiscagem: React.FC<PopupDiscagemProps> = ({
    show,
    onClose,
    onCall,
    canaisVoip,
}) => {
    const [clienteId, setClienteId] = useState<number | null>(null);
    const [cliente, setCliente] = useState<Cliente | null>(null);
    const [telefone, setTelefone] = useState<string | null>('');
    const [canalIdStr, setCanalIdStr] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<'cliente' | 'manual'>('cliente');

    const selectedCanal = useMemo(() => {
        if (!canalIdStr) return null;
        return canaisVoip.find(c => c.id.toString() === canalIdStr) || null;
    }, [canaisVoip, canalIdStr]);

    const selectedPhoneNumberFormat: PhoneNumberFormat = useMemo(() => {
        try {
            const config = JSON.parse(selectedCanal?.chave_api || '{}');
            return (config.phoneNumberFormat || 'E164') as PhoneNumberFormat;
        } catch {
            return 'E164';
        }
    }, [selectedCanal]);

    const sanitizeManualPhone = useCallback((value: string, format: PhoneNumberFormat) => {
        if (format === 'N10') {
            return value.replace(/\D+/g, '');
        }

        // E164: permitir apenas + no início e dígitos
        const keep = value.replace(/[^\d\+]+/g, '');
        const digits = keep.replace(/\+/g, '');
        return (keep.startsWith('+') ? '+' : '') + digits;
    }, []);

    const manualPhonePlaceholder = selectedPhoneNumberFormat === 'N10'
        ? 'Ex: 62911112222'
        : 'Ex: +5562911112222';

    const manualPhoneError = useMemo(() => {
        if (mode !== 'manual') return undefined;
        const value = (telefone || '').trim();
        if (!value.length) return undefined;

        if (selectedPhoneNumberFormat === 'N10') {
            if (!/^\d+$/.test(value)) return 'Use apenas dígitos (sem +).';
            if (value.length < 8) return 'Número muito curto.';
            return undefined;
        }

        if (!/^\+\d+$/.test(value)) return 'Use o formato E.164 (com + e dígitos).';
        if (value.length < 9) return 'Número muito curto.';
        return undefined;
    }, [mode, telefone, selectedPhoneNumberFormat]);

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            setClienteId(null);
            setCliente(null);
            setTelefone('');
            setCanalIdStr(canaisVoip.length > 0 ? canaisVoip[0].id.toString() : null);
            setMode('cliente');
        }
    }, [show, canaisVoip]);

    // Se estiver discando manualmente e o canal (formato) mudar, re-sanitizar
    useEffect(() => {
        if (mode !== 'manual') return;
        setTelefone(prev => sanitizeManualPhone(prev || '', selectedPhoneNumberFormat));
    }, [mode, selectedPhoneNumberFormat, sanitizeManualPhone]);

    // Load cliente details when selected
    useEffect(() => {
        if (!clienteId) {
            setCliente(null);
            setTelefone('');
            return;
        }

        const loadCliente = async () => {
            try {
                const { data } = await axios.get(`/api/cliente/${clienteId}`);
                setCliente(data.result);

                // Auto-select first phone number
                const telefoneContato = data.result.contatos?.find(
                    (c: any) => c.tipo === 'telefone' || c.tipo === 'celular' || c.tipo === 'whatsapp'
                );
                if (telefoneContato) {
                    setTelefone(telefoneContato.valor);
                }
            } catch (err) {
                console.error('Erro ao carregar cliente:', err);
            }
        };

        loadCliente();
    }, [clienteId]);

    const handleCall = useCallback(() => {
        if (!telefone || !canalIdStr) return;

        if (mode === 'manual' && manualPhoneError) return;

        setLoading(true);
        onCall({
            clienteId: clienteId || 0,
            clienteNome: cliente?.nome || telefone,
            telefone,
            canalId: parseInt(canalIdStr, 10),
        });
        setLoading(false);
        onClose();
    }, [telefone, canalIdStr, clienteId, cliente, onCall, onClose, mode, manualPhoneError]);

    const telefonesDisponiveis = cliente?.contatos
        ?.filter((c) => ['telefone', 'celular', 'whatsapp'].includes(c.tipo))
        .map((c) => ({
            id: c.valor,
            label: `${c.tipo}: ${c.valor}`,
        })) || [];

    const canaisOptions = canaisVoip.map((c) => ({
        id: c.id.toString(),
        label: c.nome,
    }));

    return (
        <Modal
            show={show}
            onClose={onClose}
            title={
                <HBox style={{ alignItems: 'center', gap: '0.5rem' }}>
                    <Icon path={mdiPhone} size={1} color="var(--tc-color-primary)" />
                    <span>Realizar Ligação</span>
                </HBox>
            }
            hideControls
            size="sm"
        >
            <div className={styles.content}>
                {/* Seleção de modo */}
                <div className={styles.modeSelector}>
                    <button
                        className={`${styles.modeButton} ${mode === 'cliente' ? styles.active : ''}`}
                        onClick={() => setMode('cliente')}
                    >
                        <Icon path={mdiAccount} size={0.8} />
                        <span>Selecionar Cliente</span>
                    </button>
                    <button
                        className={`${styles.modeButton} ${mode === 'manual' ? styles.active : ''}`}
                        onClick={() => setMode('manual')}
                    >
                        <Icon path={mdiNumeric} size={0.8} />
                        <span>Discar Manual</span>
                    </button>
                </div>

                {/* Canal VoIP */}
                <CampoSeletor
                    label="Canal VoIP"
                    value={canalIdStr}
                    onChange={setCanalIdStr}
                    options={canaisOptions}
                    disabled={canaisVoip.length <= 1}
                />

                {mode === 'cliente' ? (
                    <>
                        <CampoSeletorCliente
                            label="Cliente"
                            value={clienteId}
                            onChange={setClienteId}
                        />

                        {cliente && telefonesDisponiveis.length > 0 && (
                            <CampoSeletor
                                label="Telefone"
                                value={telefone}
                                onChange={setTelefone}
                                options={telefonesDisponiveis}
                            />
                        )}

                        {cliente && telefonesDisponiveis.length === 0 && (
                            <div className={styles.noPhone}>
                                <Icon path={mdiClose} size={0.8} color="var(--tc-color-danger)" />
                                <span>Cliente sem telefone cadastrado</span>
                            </div>
                        )}
                    </>
                ) : (
                    <CampoTexto
                        label="Número de Telefone"
                        placeholder={manualPhonePlaceholder}
                        value={telefone || ''}
                        onChange={(v: string) => setTelefone(sanitizeManualPhone(v, selectedPhoneNumberFormat))}
                        error={manualPhoneError}
                        autoFocus
                    />
                )}

                <HBox style={{ justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid var(--tc-color-gray-200)' }}>
                    <Botao variant="outline-secondary" onClick={onClose}>
                        Cancelar
                    </Botao>
                    <Botao
                        variant="success"
                        onClick={handleCall}
                        disabled={!telefone || !canalIdStr || loading || (mode === 'manual' && !!manualPhoneError)}
                        icon={mdiPhone}
                    >
                        {loading ? 'Ligando...' : 'Ligar'}
                    </Botao>
                </HBox>
            </div>
        </Modal>
    );
};

export default PopupDiscagem;
